/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {
  alpha,
  Box,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  ListSubheader,
  Select,
  MenuItem,
  SvgIconTypeMap,
  Chip,
  Divider,
  Button,
  SxProps,
  Theme,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Loader } from './Loader';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { NewTag } from './Tag';
import { dayjs } from 'util/shared/date';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { AdminContentCategory } from 'types';
import {
  SEARCH_SORT_LABELS,
  SEARCH_SORT_MODES,
  SearchSortMode,
} from 'types/search';
import { splitSnippet, TimeFilter, trimSnippetLead } from 'util/search';
import useIsMobile from 'hooks/useIsMobile';
import { Fragment } from 'react';

// Three fragments at ~160 characters each is already ~480 characters of row.
// More than this and a single long hit pushes the rest of the list off-screen.
const MAX_SNIPPETS = 3;

interface ReportItem {
  key: string;
  title: string;
  dateDisplayString: string;
  /** Only present on search results; browse rows have no fragments. */
  snippets?: string[];
}

interface CategoryOption {
  value: AdminContentCategory;
  label: string;
  icon?: OverridableComponent<SvgIconTypeMap<object, 'svg'>>;
}

interface ReportsListProps {
  loading: boolean;
  items: ReportItem[];
  selectedItemKey: string | null;
  onItemSelect: (key: string) => void;
  onItemClick?: (item: ReportItem, index: number) => void;
  IconComponent?: OverridableComponent<SvgIconTypeMap<object, 'svg'>>;
  // Category props
  selectedCategories?: AdminContentCategory[];
  categories?: CategoryOption[];
  onCategoriesChange?: (categories: AdminContentCategory[]) => void;
  // Pagination props
  showPagination?: boolean;
  currentPage?: number;
  hasNextPage?: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  paginationCondition?: boolean;
  // Date selection props
  selectedMonth?: number;
  selectedYear?: number;
  onMonthChange?: (month: number) => void;
  onYearChange?: (year: number) => void;
  // Search props
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
  /** True while a search is active: the month/year browse controls give way
   * to the sort selector. */
  searchMode?: boolean;
  searchError?: string | null;
  sortBy?: SearchSortMode;
  onSortByChange?: (sort: SearchSortMode) => void;
  // Time filter props
  timeFilter?: TimeFilter;
  onTimeFilterChange?: (filter: TimeFilter) => void;
  // Additional props
  onRefresh?: () => Promise<void>;
  onClose?: () => void;
  firstNewItemIndex?: number;
  /** Style overrides for the root container. */
  sx?: SxProps<Theme>;
}

// Fragments arrive sentinel-delimited, never as HTML: every segment goes in as
// a React text node. dangerouslySetInnerHTML here would be a live XSS hole,
// because extraction decodes entities and a note legitimately contains text
// like 'SPX < 4800'.
const renderSnippets = (snippets?: string[]) => {
  if (snippets == null || snippets.length === 0) {
    return null;
  }
  return (
    <Box
      sx={{
        mt: 0.5,
        typography: 'body2',
        color: 'text.secondary',
        // Clamped so the row stays a fixed height and the 320px list does not
        // reflow as results stream in.
        display: '-webkit-box',
        WebkitLineClamp: MAX_SNIPPETS,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
    >
      {snippets.slice(0, MAX_SNIPPETS).map((fragment, fragmentIndex) => (
        <Fragment key={fragmentIndex}>
          {fragmentIndex > 0 && ' … '}
          {trimSnippetLead(splitSnippet(fragment)).map(
            (segment, segmentIndex) =>
              segment.marked ? (
                // Weight rather than the UA stylesheet's yellow background,
                // which fights the MUI palette in dark mode.
                <Box
                  component="mark"
                  key={segmentIndex}
                  sx={{
                    bgcolor: 'transparent',
                    fontWeight: 700,
                    color: 'text.primary',
                  }}
                >
                  {segment.text}
                </Box>
              ) : (
                <span key={segmentIndex}>{segment.text}</span>
              ),
          )}
        </Fragment>
      ))}
    </Box>
  );
};

const ReportsList: React.FC<ReportsListProps> = ({
  loading,
  items,
  selectedItemKey,
  onItemSelect,
  onItemClick,
  IconComponent,
  categories,
  selectedCategories,
  onCategoriesChange,
  showPagination = false,
  currentPage = 1,
  hasNextPage = false,
  onNextPage,
  onPreviousPage,
  paginationCondition = true,
  selectedMonth,
  selectedYear,
  onMonthChange,
  onYearChange,
  searchQuery,
  onSearchQueryChange,
  searchMode = false,
  searchError = null,
  sortBy,
  onSortByChange,
  timeFilter,
  onTimeFilterChange,
  onRefresh,
  onClose,
  firstNewItemIndex,
  sx: sxOverrides,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  // Normalize category to array for consistent handling

  const handleItemClick = (item: ReportItem, index: number) => {
    if (onItemClick) {
      onItemClick(item, index);
    }
    onItemSelect(item.key);
  };

  const handleCategoryToggle = (categoryValue: AdminContentCategory) => {
    if (!onCategoriesChange) {
      return;
    }

    if (selectedCategories?.includes(categoryValue)) {
      // Remove category if it's already selected (but keep at least one)
      if (selectedCategories?.length > 1) {
        const newCategories = selectedCategories.filter(
          (cat) => cat !== categoryValue,
        );
        onCategoriesChange(newCategories);
      }
    } else {
      // Add category if it's not selected
      const newCategories = [...(selectedCategories ?? []), categoryValue];
      onCategoriesChange(newCategories);
    }
  };

  // Generate months array
  const months = dayjs.months();

  // Generate years array (current year and previous years)
  const currentYear = dayjs().year();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const categorySelector = categories && categories.length > 0 && (
    <Box sx={{ p: 2, pb: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, display: 'block' }}
      >
        Categories
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {categories.map((cat) => {
          const isSelected = selectedCategories?.includes(cat.value);
          return (
            <Chip
              key={cat.value}
              label={cat.label}
              variant={isSelected ? 'filled' : 'outlined'}
              color={isSelected ? 'primary' : 'default'}
              size="small"
              onClick={() => handleCategoryToggle(cat.value)}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                fontWeight: isSelected ? 600 : 400,
                borderWidth: isSelected ? 1 : 1,
                borderColor: isSelected
                  ? theme.palette.primary.main
                  : alpha(theme.palette.text.secondary, 0.3),
                backgroundColor: isSelected
                  ? alpha(theme.palette.primary.main, 0.3)
                  : 'transparent',
                color: isSelected
                  ? theme.palette.primary.contrastText
                  : theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: isSelected
                    ? theme.palette.primary.dark
                    : alpha(theme.palette.primary.main, 0.08),
                  borderColor: isSelected
                    ? theme.palette.primary.dark
                    : theme.palette.primary.main,
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[2],
                },
                '&:active': {
                  transform: 'translateY(0px)',
                  boxShadow: theme.shadows[1],
                },
              }}
            />
          );
        })}
      </Box>
      {selectedCategories && selectedCategories.length > 0 && (
        <Typography
          variant="caption"
          color="primary"
          sx={{
            mt: 1,
            display: 'block',
            fontWeight: 500,
            fontSize: '0.75rem',
          }}
        >
          {selectedCategories.length} of {categories.length} categories selected
        </Typography>
      )}
    </Box>
  );

  const handleRefresh = () => {
    if (onRefresh) {
      void onRefresh();
    }
  };

  const selectSx = {
    fontSize: '0.85rem',
    color: theme.palette.text.primary,
    '& .MuiSelect-select': {
      py: 0.5,
      px: 1,
    },
    '&::before, &::after': {
      borderColor: alpha(theme.palette.text.primary, 0.2),
    },
    '& .MuiSelect-icon': {
      color: theme.palette.text.secondary,
    },
  };

  // Rendered in both the browse row and the search row: the AM/PM split is
  // about which note you want, which a search does not answer.
  const timeFilterSelect = (sx: SxProps<Theme>) =>
    timeFilter != null && onTimeFilterChange != null ? (
      <Select
        variant="standard"
        value={timeFilter}
        onChange={(e) => {
          onTimeFilterChange(e.target.value as TimeFilter);
        }}
        size="small"
        SelectDisplayProps={{ 'aria-label': 'Filter by time of day' }}
        sx={sx}
      >
        <MenuItem value="all">All</MenuItem>
        <MenuItem value="am">AM</MenuItem>
        <MenuItem value="pm">PM</MenuItem>
      </Select>
    ) : null;

  const refreshSubheader = (
    <ListSubheader
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        py: 1,
        px: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          onClick={handleRefresh}
          variant="text"
          startIcon={<RefreshIcon sx={{ fontSize: '16px !important' }} />}
          sx={{
            color: theme.palette.primary.main,
            '&:hover': { color: theme.palette.primary.dark },
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 13,
            minWidth: 'auto',
            px: 1,
          }}
        >
          Refresh
        </Button>
        <Button
          onClick={onClose}
          variant="text"
          endIcon={<CloseIcon sx={{ fontSize: '16px !important' }} />}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': { color: theme.palette.text.primary },
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 13,
            minWidth: 'auto',
            px: 1,
          }}
        >
          Close
        </Button>
      </Box>

      {onSearchQueryChange != null && (
        <TextField
          value={searchQuery ?? ''}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          placeholder="Search"
          size="small"
          fullWidth
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  fontSize="small"
                  sx={{ color: theme.palette.text.secondary }}
                />
              </InputAdornment>
            ),
            endAdornment:
              (searchQuery ?? '').length > 0 ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="clear search"
                    onClick={() => onSearchQueryChange('')}
                    sx={{
                      color: theme.palette.text.secondary,
                      '&:hover': { color: theme.palette.text.primary },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
          }}
          sx={{ mt: 1, '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
        />
      )}

      {!searchMode && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            mt: 1,
          }}
        >
          <Select
            variant="standard"
            value={selectedMonth ?? dayjs().month()}
            onChange={(e) => {
              onMonthChange?.(parseInt(e.target.value as string));
            }}
            size="small"
            sx={{ flex: 1, minWidth: 0, ...selectSx }}
          >
            {months
              .slice(
                0,
                selectedYear === dayjs().year()
                  ? dayjs().month() + 1
                  : months.length,
              )
              .map((month, i) => (
                <MenuItem value={i} key={i}>
                  {month}
                </MenuItem>
              ))}
          </Select>

          <Select
            variant="standard"
            value={selectedYear ?? dayjs().year()}
            onChange={(e) => {
              const newYear = parseInt(e.target.value as string);
              if (
                newYear === dayjs().year() &&
                (selectedMonth ?? dayjs().month()) > dayjs().month()
              ) {
                onMonthChange?.(dayjs().month());
              }
              onYearChange?.(newYear);
            }}
            size="small"
            sx={{ flex: 1, minWidth: 0, ...selectSx }}
          >
            {years.map((year) => (
              <MenuItem value={year} key={year}>
                {year}
              </MenuItem>
            ))}
          </Select>

          {timeFilterSelect({ flex: 1, minWidth: 0, ...selectSx })}
        </Box>
      )}

      {searchMode && (sortBy != null || timeFilter != null) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 0.75,
            mt: 1,
          }}
        >
          {sortBy != null && onSortByChange != null && (
            <Select
              variant="standard"
              value={sortBy}
              onChange={(e) => {
                onSortByChange(e.target.value as SearchSortMode);
              }}
              size="small"
              // The row is otherwise unlabelled, so the closed control has to
              // say what it is rather than just naming the current order.
              renderValue={(value) => `Sort: ${SEARCH_SORT_LABELS[value]}`}
              SelectDisplayProps={{ 'aria-label': 'Sort results' }}
              // Given the larger share of the row: its labels carry the 'Sort:'
              // prefix, where the time filter only ever shows two letters.
              sx={{ flex: 2, minWidth: 0, ...selectSx }}
            >
              {SEARCH_SORT_MODES.map((mode) => (
                <MenuItem value={mode} key={mode}>
                  {SEARCH_SORT_LABELS[mode]}
                </MenuItem>
              ))}
            </Select>
          )}

          {timeFilterSelect({ flex: 1, minWidth: 0, ...selectSx })}
        </Box>
      )}
    </ListSubheader>
  );

  // Where the rows would have been. In the subheader these read as part of the
  // controls; here they read as the answer to the search.
  const searchMessageSx = {
    width: '100%',
    px: 3,
    py: 6,
    textAlign: 'center',
    fontSize: '1.1rem',
    fontWeight: 500,
  } as const;

  const searchMessage =
    !searchMode || loading ? null : searchError != null ? (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Typography variant="h6" color="error" sx={searchMessageSx}>
          {searchError}
        </Typography>
      </Box>
    ) : (items ?? []).length === 0 ? (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Typography variant="h6" color="text.secondary" sx={searchMessageSx}>
          No matches
        </Typography>
      </Box>
    ) : null;

  // The Loader swaps its children for a spinner, so it must wrap only the
  // items. Wrapping the List would unmount the subheader on every load,
  // tearing down the search input mid-keystroke and dropping focus.
  const notesList = (
    <List
      subheader={refreshSubheader}
      sx={{
        '& .MuiListItemButton-root': {
          borderRadius: 0,
          mx: 1,
          mb: 0.5,
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.16),
            },
          },
        },
      }}
    >
      <Loader isLoading={loading}>
        {searchMessage}
        {(items || []).map((item: ReportItem, index: number) => {
          return (
            <ListItemButton
              selected={selectedItemKey != null && selectedItemKey === item.key}
              onClick={() => handleItemClick(item, index)}
              key={`list-${item.key}`}
            >
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <ListItemIcon sx={{ minWidth: 'auto' }}>
                  {IconComponent ? (
                    <IconComponent fontSize="small" color="primary" />
                  ) : (
                    <div style={{ width: 20, height: 20 }} />
                  )}
                </ListItemIcon>
              </ListItemAvatar>
              <ListItemText
                secondaryTypographyProps={{ component: 'div' }}
                primary={
                  <Typography
                    variant="body1"
                    fontWeight={selectedItemKey === item.key ? 600 : 500}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {item.title}
                  </Typography>
                }
                secondary={renderSnippets(item.snippets)}
              />
              {(firstNewItemIndex !== undefined
                ? index < firstNewItemIndex
                : index === 0) && <NewTag />}
            </ListItemButton>
          );
        })}
      </Loader>
    </List>
  );

  return (
    <Box
      sx={{
        ...(isMobile
          ? { width: '280px' }
          : {
              // The width a flexible list settled at once a real note pushed
              // back on it. Pinned, because the sibling content pane is sized
              // from its own content: left to shrink, the list would spring
              // back to its full width every time a spinner replaced the note.
              minWidth: '320px',
              width: '320px',
            }),
        flexShrink: 0,
        height: {
          xs: '100%',
          lg: 'calc(100vh - 170px)',
        },
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: theme.shadows[2],
        borderLeft: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ...sxOverrides,
      }}
    >
      {categorySelector}
      {categorySelector && (
        <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.2) }} />
      )}

      <Box sx={{ flex: 1, overflow: 'auto' }}>{notesList}</Box>

      {/* Search pages the same way browse does, so the arrows are shared. The
          searchMessage gate stands in for the old !searchMode one: it is null in
          browse mode, and in search mode it means there are no rows to page. */}
      {showPagination &&
        searchMessage == null &&
        items != null &&
        !loading &&
        paginationCondition && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.5),
            }}
          >
            <IconButton
              data-ph-capture-attribute-element="reports-prev"
              onClick={onPreviousPage}
              color="primary"
              disabled={currentPage <= 1}
              size="small"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography
              color="primary"
              sx={{
                mx: 2,
                fontSize: '0.875rem',
                fontWeight: 600,
                minWidth: '2ch',
                textAlign: 'center',
              }}
            >
              {currentPage}
            </Typography>
            <IconButton
              data-ph-capture-attribute-element="reports-next"
              onClick={onNextPage}
              color="primary"
              disabled={!hasNextPage}
              size="small"
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
    </Box>
  );
};

export default ReportsList;
